import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Users,
  BookOpen,
  Filter,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Clock,
  Minus,
  FileText,
  Download,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";

const AttendanceListPage = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [selectedBatch, setSelectedBatch] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");

  const attendanceStatuses = {
    present: {
      label: "Present",
      color: "text-emerald-400",
      bg: "bg-emerald-500/20",
      icon: Check,
    },
    absent: {
      label: "Absent",
      color: "text-rose-400",
      bg: "bg-rose-500/20",
      icon: X,
    },
    late: {
      label: "Late",
      color: "text-amber-400",
      bg: "bg-amber-500/20",
      icon: Clock,
    },
    half_day: {
      label: "Half Day",
      color: "text-blue-400",
      bg: "bg-blue-500/20",
      icon: Minus,
    },
    leave: {
      label: "Leave",
      color: "text-purple-400",
      bg: "bg-purple-500/20",
      icon: FileText,
    },
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, [pageNo, pageSize, selectedBatch, status, fromDate, toDate]);

  const fetchBatches = async () => {
    try {
      const { data } = await axios.get(`${server}/batch/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (data.success && data.data) {
        setBatches(data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch batches");
    }
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const params = {
        pageNo,
        pageSize,
        ...(selectedBatch && { batchId: selectedBatch }),
        ...(status && { status }),
        ...(fromDate && { fromDate: new Date(fromDate).toISOString() }),
        ...(toDate && { toDate: new Date(toDate).toISOString() }),
      };

      const { data } = await axios.get(`${server}/attendance/all`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (data.success) {
        setAttendanceData(data.data || []);
        // Assuming API returns total count or pages. adapting based on structure
        // If API doesn't return total count, we might need to handle pagination differently
        if (data.totalCount) {
          setTotalPages(Math.ceil(data.totalCount / pageSize));
        } else {
          // Fallback if totalCount is missing (common in some APIs)
          setTotalPages(data.data?.length === pageSize ? pageNo + 1 : pageNo);
        }
      }
    } catch (error) {
      toast.error("Failed to fetch attendance logs");
    } finally {
      setLoading(false);
    }
  };

  const setThisWeek = () => {
    const today = new Date();
    const day = today.getDay(); // 0 is Sunday
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
    const monday = new Date(today.setDate(diff));
    const sunday = new Date(today.setDate(monday.getDate() + 6));

    setFromDate(monday.toISOString().split("T")[0]);
    setToDate(sunday.toISOString().split("T")[0]);
  };

  const setThisMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 2);

    setFromDate(firstDay.toISOString().split("T")[0]);
    setToDate(today.toISOString().split("T")[0]);
  };

  const setLastMonth = () => {
    const today = new Date();

    const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 2);
    const lastDay = new Date(today.getFullYear(), today.getMonth() - 0, 1);

    setFromDate(firstDay.toISOString().split("T")[0]);
    setToDate(lastDay.toISOString().split("T")[0]);
  };

  const clearFilters = () => {
    setSelectedBatch("");
    setStatus("");
    setFromDate("");
    setToDate("");
    setPageNo(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 font-sans text-slate-100">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-400 flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-400" />
              Attendance Logs
            </h1>
            <p className="text-slate-400 mt-1">
              View and filter detailed attendance records
            </p>
          </div>
          <button
            onClick={fetchAttendance}
            className="p-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition w-fit border border-slate-700 text-slate-300"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Filters Panel */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700 shadow-lg">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2 text-slate-300 font-semibold uppercase text-xs tracking-wider">
              <Filter className="w-4 h-4" /> Filters
            </div>

            {/* Date Presets */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={setThisWeek}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs rounded-lg transition-colors border border-slate-600"
              >
                This Week
              </button>
              <button
                onClick={setThisMonth}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs rounded-lg transition-colors border border-slate-600"
              >
                This Month
              </button>
              <button
                onClick={setLastMonth}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs rounded-lg transition-colors border border-slate-600"
              >
                Last Month
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Batch
              </label>
              <select
                value={selectedBatch}
                onChange={(e) => setSelectedBatch(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors text-sm"
              >
                <option value="">All Batches</option>
                {batches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.batchName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors text-sm"
              >
                <option value="">All Statuses</option>
                {Object.keys(attendanceStatuses).map((key) => (
                  <option key={key} value={key}>
                    {attendanceStatuses[key].label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors text-sm"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden shadow-xl">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : attendanceData.length === 0 ? (
            <div className="text-center py-20">
              <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No attendance records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-900/50 border-b border-slate-700">
                    <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Date/Time
                    </th>
                    <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Batch
                    </th>
                    <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Remarks
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {attendanceData.map((record, index) => {
                    const statusConfig = attendanceStatuses[record.status] || {
                      label: record.status,
                      color: "text-gray-400",
                      bg: "bg-gray-700",
                      icon: FileText,
                    };
                    const StatusIcon = statusConfig.icon;
                    return (
                      <tr
                        key={index}
                        className="hover:bg-slate-700/30 transition-colors"
                      >
                        <td className="p-4 text-sm text-slate-300">
                          {new Date(record.attendanceDate).toLocaleDateString()}
                          <span className="block text-xs text-slate-500">
                            {new Date(
                              record.attendanceDate
                            ).toLocaleTimeString()}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="font-medium text-white">
                            {record.studentName || `ID: ${record.studentId}`}
                          </div>
                          <div className="text-xs text-slate-500">
                            {record.enrollmentNumber}
                          </div>
                        </td>
                        <td className="p-4 text-sm text-slate-300">
                          {record.batchName || `Batch ${record.batchId}`}
                        </td>
                        <td className="p-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color} border border-white/5`}
                          >
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td
                          className="p-4 text-sm text-slate-400 italic max-w-xs truncate"
                          title={record.remarks}
                        >
                          {record.remarks || "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-slate-700 bg-slate-900/30">
            <div className="text-sm text-slate-500">Page {pageNo}</div>
            <div className="flex gap-2">
              <button
                onClick={() => setPageNo(Math.max(1, pageNo - 1))}
                disabled={pageNo === 1}
                className="p-2 rounded-lg bg-slate-800 text-slate-300 border border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPageNo(pageNo + 1)}
                disabled={attendanceData.length < pageSize}
                className="p-2 rounded-lg bg-slate-800 text-slate-300 border border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceListPage;
